/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { CoreAppStore } from '@opengeoweb/store';
import {
  layerActions,
  layerTypes,
  uiSelectors,
  uiTypes,
} from '@opengeoweb/store';
import { LayerType, webmapUtils } from '@opengeoweb/webmap';
import DatasetRow from './DatasetRow';
import type { DiscoveryMetadataFeature } from '../../store/types';
import { DatasetFormat } from '../../store/types';
import { useFetchGeoJSON } from '../../utils/dataexplorer-api/hooks';

const getDatasetFormat = (
  feature: DiscoveryMetadataFeature,
): DatasetFormat | null => {
  if (feature.properties.formats) {
    if (
      feature.properties.formats.some(
        (format) => format.name === DatasetFormat.WMS,
      )
    ) {
      return DatasetFormat.WMS;
    }
    if (
      feature.properties.formats.some(
        (format) => format.name === DatasetFormat.GeoJSON,
      )
    ) {
      return DatasetFormat.GeoJSON;
    }
  }
  return null;
};

// Checks the dataset links to get the layer name and service URL depending on the dataset format
const getLayerProperties = (
  feature: DiscoveryMetadataFeature,
  datasetFormat: DatasetFormat | null,
): { layerName: string | null; serviceUrl: string | null } => {
  let layerName = null;
  let serviceUrl = null;

  if (datasetFormat === DatasetFormat.WMS) {
    const dataLink = feature.links.find((link) => link.rel === 'data');
    if (dataLink) {
      const url = new URL(dataLink.href);
      serviceUrl = url.toString();
      const nameParam =
        url.searchParams.get('layers') ||
        url.searchParams.get('LAYERS') ||
        url.searchParams.get('layer') ||
        url.searchParams.get('LAYER');
      if (nameParam) {
        layerName = nameParam;
      }
    }
  }

  if (datasetFormat === DatasetFormat.GeoJSON) {
    const dataLink = feature.links.find(
      (link) =>
        link.type === 'application/geo+json' && link.href.startsWith('http'),
    );
    if (dataLink) {
      const url = new URL(dataLink.href);
      serviceUrl = url.toString();

      const name = dataLink.title;
      if (name) {
        layerName = name;
      }
    }
  }

  return { layerName, serviceUrl };
};

// Checks if a layer with a name and service URL matching the dataset already exists
const getExistingLayerAndIndex = (
  mapLayers: layerTypes.Layer[],
  layerName: string | null,
  serviceUrl: string | null,
): { foundLayerIndex: number; foundLayer: layerTypes.Layer | null } => {
  let foundLayerIndex = -1;
  let foundLayer: layerTypes.Layer | null = null;

  if (mapLayers) {
    foundLayerIndex = mapLayers?.findIndex(
      (lr) => lr.name === layerName && lr.service === serviceUrl,
    )!;
    if (foundLayerIndex !== -1) {
      const layer = mapLayers[foundLayerIndex];
      foundLayer = layer;
    }
  }

  return { foundLayer, foundLayerIndex };
};

interface DatasetRowConnectProps {
  feature: DiscoveryMetadataFeature;
  mapLayers: layerTypes.Layer[];
  searchFilter: string;
}

const DatasetRowConnect: React.FC<DatasetRowConnectProps> = ({
  feature,
  mapLayers,
  searchFilter,
}: DatasetRowConnectProps) => {
  const dispatch = useDispatch();

  const activeMapId = useSelector((store: CoreAppStore) =>
    uiSelectors.getDialogMapId(store, uiTypes.DialogTypes.DataExplorer),
  );

  const [geoJsonUrl, setGeoJsonUrl] = useState<string | null>(null);

  const { data: geoJsonData } = useFetchGeoJSON(geoJsonUrl!);

  // Get the dataset format from the feature properties
  const datasetFormat = getDatasetFormat(feature);
  const isGeoJSON = datasetFormat === DatasetFormat.GeoJSON;
  const isWMS = datasetFormat === DatasetFormat.WMS;

  // Get layer name and service URL from the dataset feature properties
  const { layerName, serviceUrl } = getLayerProperties(feature, datasetFormat);

  // Check if the dataset already has a layer associated with it
  const { foundLayerIndex, foundLayer } = getExistingLayerAndIndex(
    mapLayers,
    layerName,
    serviceUrl,
  );

  const addDisabled = (!isWMS && !isGeoJSON) || !serviceUrl || !layerName;

  const dispatchAddLayer = React.useCallback(
    ({ layerId, layer, origin }: layerTypes.AddLayerPayload) =>
      dispatch(
        layerActions.addLayer({
          mapId: activeMapId,
          layerId,
          layer,
          origin,
        }),
      ),
    [dispatch, activeMapId],
  );

  useEffect(() => {
    const addGeoJsonLayer = (): void => {
      if (geoJsonUrl && geoJsonData && !foundLayer) {
        const layer = {
          geojson: geoJsonData,
          service: serviceUrl,
          name: layerName,
          id: webmapUtils.generateLayerId(),
          layerType: LayerType.featureLayer,
          isSelectable: true,
        } as layerTypes.Layer;

        dispatchAddLayer({
          layerId: layer.id,
          layer,
          origin: layerTypes.LayerActionOrigin.dataExplorer,
        } as layerTypes.AddLayerPayload);

        setGeoJsonUrl(null);
      }
    };

    addGeoJsonLayer();
  }, [
    geoJsonData,
    dispatchAddLayer,
    serviceUrl,
    layerName,
    foundLayer,
    geoJsonUrl,
  ]);

  const deleteLayer = React.useCallback(() => {
    dispatch(
      layerActions.layerDelete({
        mapId: activeMapId,
        layerId: foundLayer?.id!,
        layerIndex: foundLayerIndex,
        origin: layerTypes.LayerActionOrigin.layerManager,
      }),
    );
  }, [dispatch, activeMapId, foundLayer?.id, foundLayerIndex]);

  // Add layer to the map
  const addLayer = React.useCallback((): void => {
    if (!serviceUrl || !layerName || !datasetFormat) {
      return;
    }

    if (datasetFormat === DatasetFormat.WMS) {
      const layer = {
        service: serviceUrl,
        name: layerName,
        id: webmapUtils.generateLayerId(),
        layerType: LayerType.mapLayer,
      } as layerTypes.Layer;

      dispatchAddLayer({
        layerId: layer.id,
        layer,
        origin: layerTypes.LayerActionOrigin.dataExplorer,
      } as layerTypes.AddLayerPayload);
    }
    if (datasetFormat === DatasetFormat.GeoJSON) {
      // To add GeoJSON layer, we set the URL for tanstack hook to use
      setGeoJsonUrl(serviceUrl);
    }
  }, [dispatchAddLayer, datasetFormat, layerName, serviceUrl]);

  return (
    <DatasetRow
      addDisabled={addDisabled}
      addLayer={addLayer}
      deleteLayer={deleteLayer}
      feature={feature}
      foundLayer={foundLayer}
      key={feature.id}
      layerName={layerName}
      searchFilter={searchFilter}
    />
  );
};

export default DatasetRowConnect;
